import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Phone, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import { COMPANY } from "@/lib/data";
import { useDetailModal } from "@/components/DetailModalContext";
import { useDemoModal } from "@/components/DemoModalContext";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp className={className} strokeWidth={1.5} />;
};

export default function DetailModal() {
  const { item, closeDetail } = useDetailModal();
  const { setOpen } = useDemoModal();

  const openDemo = () => {
    closeDetail();
    setTimeout(() => setOpen(true), 180);
  };

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          data-testid="detail-modal"
        >
          <div
            className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
            onClick={closeDetail}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-[0_40px_100px_rgba(0,0,0,0.25)]"
            data-lenis-prevent
          >
            <button
              onClick={closeDetail}
              aria-label="Kapat"
              className="absolute right-5 top-5 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-ink/70 shadow-sm backdrop-blur transition-colors hover:bg-white"
              data-testid="detail-modal-close"
            >
              <X className="h-4.5 w-4.5" />
            </button>

            {/* Header */}
            {item.image ? (
              <div className="relative h-44 w-full overflow-hidden md:h-52">
                <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                <div className="absolute bottom-5 left-7 flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 text-orange">
                    <Icon name={item.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="text-2xl font-extrabold tracking-tight text-white">{item.title}</h3>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 px-7 pt-9 md:px-9">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange/10 text-orange">
                  <Icon name={item.icon} className="h-7 w-7" />
                </span>
                <h3 className="text-2xl font-extrabold tracking-tight text-ink">{item.title}</h3>
              </div>
            )}

            {/* Body */}
            <div className="px-7 py-7 md:px-9 md:py-8">
              <p className="text-base leading-relaxed text-ink/70">{item.long || item.desc}</p>

              {item.benefits?.length > 0 && (
                <div className="mt-6">
                  <p className="overline">Öne Çıkanlar</p>
                  <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {item.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lime/20 text-[#7fa021]">
                          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </span>
                        <span className="text-sm leading-snug text-ink/80">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Persuasion strip */}
              <div className="mt-7 rounded-2xl bg-cloud p-5 text-sm text-ink/70">
                <span className="font-semibold text-ink">Yetkili AKINSOFT Bayisi</span> güvencesiyle
                kurulum, eğitim ve 7/24 teknik destek dahil. İşletmenize özel demoyu ücretsiz
                deneyin.
              </div>

              {/* CTAs */}
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <button onClick={openDemo} className="btn-primary flex-1" data-testid="detail-demo-btn">
                  Demo Talebi Oluştur <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="btn-secondary flex-1"
                  data-testid="detail-call-btn"
                >
                  <Phone className="h-4 w-4" /> Bizi Arayın
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
